/**
 * Company › Owned arches › reviewing an offer (ADR-026).
 *
 * Accepting shows what the acceptance does — the Company's payer is paid its
 * earnings (and pays its per-arche costs), every collaborator who keeps access
 * and with what role, and that the Company then governs it — and sends back
 * exactly what it showed; if any of that changed meanwhile the server refuses
 * and the screen reloads (DISCLOSURE_CHANGED). Owners decide.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { ARCHE_ROLE_LABEL } from '../../lib/arche-governance-labels';
import { companyErrorCode } from '../../lib/company-errors';
import {
  useCompanyArcheTransferDisclosure,
  useDecideCompanyArcheTransfer,
} from '../../hooks/useArcheGovernance';
import { describeError, personLabel } from './shared';

export function OwnedArcheTransferReview({
  companyId,
  companyName,
  transferId,
  onClose,
}: {
  companyId: string;
  companyName: string;
  transferId: string;
  onClose: () => void;
}) {
  const disclosure = useCompanyArcheTransferDisclosure(companyId, transferId);
  const decide = useDecideCompanyArcheTransfer(companyId);

  async function answer(decision: 'accept' | 'decline'): Promise<void> {
    const d = disclosure.data;
    if (!d) return;
    try {
      await decide.mutateAsync({
        transferId,
        decision,
        expected:
          decision === 'accept'
            ? {
                billingUserId: d.billingUserId,
                collaborators: d.collaborators.map((c) => ({ userId: c.userId, role: c.roleNow })),
              }
            : undefined,
      });
      toast({
        title:
          decision === 'accept'
            ? `${d.archeName} now belongs to ${companyName}`
            : `Declined ${d.archeName}`,
      });
      onClose();
    } catch (err) {
      // The terms changed under the screen: show the new ones instead.
      if (companyErrorCode(err) === 'DISCLOSURE_CHANGED') void disclosure.refetch();
      toast({ title: 'Could not answer', description: describeError(err), variant: 'destructive' });
    }
  }

  const d = disclosure.data;
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{d ? `Accept “${d.archeName}”?` : 'Review offer'}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {disclosure.isLoading && <p>Loading…</p>}
              {disclosure.isError && (
                <p className="text-destructive">{describeError(disclosure.error)}</p>
              )}
              {d && (
                <>
                  <p>
                    Offered by <span className="font-medium">{personLabel(d.proposedBy)}</span>. If
                    you accept:
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>
                      its earnings are paid to{' '}
                      <span className="font-medium">{personLabel(d.billingUser)}</span>, this
                      Company&apos;s payer — who also pays its per-arche costs from then on (asset
                      library, content packs, generated docs);
                    </li>
                    <li>
                      {companyName}&apos;s owners and admins manage it — its team, who can use it,
                      its settings;
                    </li>
                    <li>these people keep access to it:</li>
                  </ul>
                  {d.collaborators.length === 0 ? (
                    <p className="pl-5 text-muted-foreground">Nobody else.</p>
                  ) : (
                    <ul className="divide-y rounded-md border">
                      {d.collaborators.map((c) => (
                        <li
                          key={c.userId}
                          className="flex items-center justify-between px-3 py-1.5"
                        >
                          <span>{personLabel(c)}</span>
                          <span className="text-xs text-muted-foreground">
                            {c.roleNow === c.roleAfter
                              ? ARCHE_ROLE_LABEL[c.roleAfter]
                              : `${ARCHE_ROLE_LABEL[c.roleNow]} → ${ARCHE_ROLE_LABEL[c.roleAfter]}`}
                            {c.roleAfter === 'publisher' && ' · can publish'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!d.transferEnabled && (
                    <p className="text-amber-700">
                      Accepting arches is paused right now. The offer stays open.
                    </p>
                  )}
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <Button
            variant="outline"
            disabled={!d || decide.isPending}
            onClick={() => void answer('decline')}
          >
            Decline
          </Button>
          <AlertDialogAction
            disabled={!d || decide.isPending || !d.transferEnabled}
            onClick={(e) => {
              e.preventDefault();
              void answer('accept');
            }}
          >
            Accept
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
